'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { Icon } from '@benzinga/core-ui';
import type { Section, SectionLink, PageContext } from '../../entities';
import { getPremiumServiceHref, type PremiumServiceAccess, type PremiumServiceKey } from '../../premiumServiceLinks';
import { BenzingaLogo } from '@benzinga/logos-ui';
import { SectionMenu } from './SectionMenu';
import styles from './SiteNav.module.scss';

export interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  sections: Section[];
  variant?: 'default' | 'dark';
  premiumAccess: PremiumServiceAccess;
  pageContext?: PageContext;
  onSectionOpen?: (section: Section) => void;
  onSectionLinkClick?: (section: Section, link: SectionLink) => void;
  onPremiumClick?: (service: PremiumServiceKey) => void;
  hidePremium?: boolean;
}

const BURGER_BREAKPOINT = '(min-width: 1081px)';

const PREMIUM_ITEMS: { key: PremiumServiceKey; product: string; description: string }[] = [
  { key: 'edge', product: 'Edge', description: 'Ratings, signals & screens' },
  { key: 'pro', product: 'Pro', description: 'Real-time news & data terminal' },
  { key: 'research', product: 'Research', description: 'Trade alerts & newsletters' },
  { key: 'apis', product: 'APIs', description: 'Market data licensing' },
];

export const MobileMenu = ({
  open,
  onClose,
  sections,
  variant = 'default',
  premiumAccess,
  pageContext,
  onSectionOpen,
  onSectionLinkClick,
  onPremiumClick,
  hidePremium = false,
}: MobileMenuProps) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [drilled, setDrilled] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const sectionPaneRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const activeSection = sections.find(section => section.key === activeKey) ?? null;
  const logoVariant = variant === 'dark' ? 'light' : 'dark';

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!open || !root) return;
    root.style.setProperty('--menu-top', `${root.getBoundingClientRect().top}px`);
    setDrilled(false);
  }, [open]);

  const openSection = (section: Section) => {
    setActiveKey(section.key);
    setDrilled(true);
    onSectionOpen?.(section);
    sectionPaneRef.current?.scrollTo({ top: 0 });
    requestAnimationFrame(() => backRef.current?.focus({ preventScroll: true }));
  };

  const goBack = () => {
    setDrilled(false);
    rootRef.current?.querySelector<HTMLButtonElement>(`[data-section="${activeKey}"]`)?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (drilled) goBack();
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    if (!open) return undefined;
    const mq = window.matchMedia(BURGER_BREAKPOINT);
    if (mq.matches) onClose();
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) onClose();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [open, onClose]);

  return (
    <div
      ref={rootRef}
      className="group pointer-events-none absolute left-0 right-0 top-full z-[900] opacity-0 transition-opacity duration-150 ease-out data-[open]:pointer-events-auto data-[open]:opacity-100 motion-reduce:transition-none"
      data-open={open || undefined}
      inert={!open}
    >
      <div className="absolute left-0 right-0 top-0 h-screen bg-[rgba(10,15,20,0.4)]" onClick={onClose} />

      <div className="relative z-[1] h-[calc(100dvh-var(--menu-top,96px))] overflow-hidden bg-[var(--hubnav-panel-bg,#ffffff)] text-[var(--hubnav-fg,#0b1620)] -translate-y-1.5 transition-transform duration-[180ms] ease-out group-data-[open]:translate-y-0 motion-reduce:transition-none">
        <div
          className="grid h-full grid-cols-[100%_100%] transition-transform duration-[320ms] ease-[cubic-bezier(0.22,1,0.36,1)] data-[drilled]:-translate-x-full motion-reduce:transition-none"
          data-drilled={drilled || undefined}
        >
          <div className="h-full min-w-0 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] [-webkit-overflow-scrolling:touch]" inert={drilled}>
            <nav className="flex flex-col px-0 pb-3 pt-2" aria-label="Primary">
              {sections.map(section => (
                <button
                  key={section.key}
                  className={`${styles.hairline} relative flex w-full min-h-[72px] cursor-pointer items-center gap-4 border-0 bg-transparent px-5 py-3 text-left font-[inherit] text-[var(--hubnav-fg,#0b1b2b)] [-webkit-tap-highlight-color:transparent] transition-colors duration-100 ease-out active:bg-[var(--hubnav-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--hubnav-accent)]`}
                  type="button"
                  data-section={section.key}
                  onClick={() => openSection(section)}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <span className="text-[17px] font-bold leading-tight tracking-[-0.01em]">{section.label}</span>
                    {section.blurb ? (
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] leading-snug text-[var(--hubnav-muted,#5b6776)]">
                        {section.blurb}
                      </span>
                    ) : null}
                  </span>
                  <Icon className="h-3 w-3 flex-none text-[var(--hubnav-muted,#5b6776)]" icon={faChevronRight} />
                </button>
              ))}
            </nav>

            {!hidePremium ? (
            <nav className="mt-1 flex flex-col border-t border-[var(--hubnav-line,#e5e9ef)] px-0 pb-4 pt-2" aria-label="Premium Services">
              <p className="mx-5 mb-0.5 mt-2 text-xs font-bold uppercase tracking-[0.06em] text-[var(--hubnav-muted,#5b6776)]">
                Premium Services
              </p>
              {PREMIUM_ITEMS.map(item => (
                <a
                  key={item.key}
                  className={`${styles.hairline} relative flex min-h-16 items-center gap-3.5 px-5 py-2.5 text-inherit no-underline [-webkit-tap-highlight-color:transparent] transition-colors duration-100 ease-out data-[product=edge]:[--premium-accent:#3f83f8] data-[product=pro]:[--premium-accent:#1a79ff] data-[product=research]:[--premium-accent:#ff5a1f] data-[product=apis]:[--premium-accent:#e0474c] active:bg-[var(--hubnav-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--hubnav-accent)]`}
                  data-product={item.key}
                  href={getPremiumServiceHref(item.key, premiumAccess)}
                  onClick={() => onPremiumClick?.(item.key)}
                >
                  <span
                    className="h-10 w-[3px] flex-none rounded-full bg-[var(--premium-accent,var(--hubnav-accent))]"
                    aria-hidden="true"
                  />
                  <span className="flex min-w-0 flex-col gap-[3px]">
                    <span className="inline-flex items-center gap-1.5 [&_.benzinga-logo]:h-[13px] [&_.benzinga-logo]:w-auto [&_.benzinga-logo]:min-w-0 [&_.benzinga-logo]:max-w-none [&_.benzinga-logo]:flex-none [&_.benzinga-logo_svg]:h-[13px] [&_.benzinga-logo_svg]:w-auto">
                      <BenzingaLogo variant={logoVariant} />
                      <span className="text-base font-bold leading-tight tracking-[-0.01em] text-[var(--premium-accent,var(--hubnav-fg))]">
                        {item.product}
                      </span>
                    </span>
                    <span className="text-[13px] leading-snug text-[var(--hubnav-muted,#5b6776)] text-pretty">
                      {item.description}
                    </span>
                  </span>
                </a>
              ))}
            </nav>
            ) : null}
          </div>

          <div
            ref={sectionPaneRef}
            className="h-full min-w-0 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] [-webkit-overflow-scrolling:touch]"
            inert={!drilled}
          >
            <div className="sticky top-0 z-[2] flex h-[52px] items-center bg-[var(--hubnav-util-bg)] px-2">
              <button
                ref={backRef}
                className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-3 font-[inherit] text-sm font-semibold text-[var(--hubnav-util-fg)] active:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--hubnav-util-sym)]"
                type="button"
                onClick={goBack}
              >
                <Icon className="h-3 w-3" icon={faChevronLeft} />
                All sections
              </button>
            </div>
            {activeSection ? (
              <SectionMenu section={activeSection} pageContext={pageContext} onLinkClick={onSectionLinkClick} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
